// Migration 0047 — TanStack Query hook wrapping GET /api/v1/account/free-claim.
//
// The weekly free-dossier claim status drives the shop's free-claim slot +
// the AppShell "free claim available" nav dot:
//
//   * "claimable"        — the account has not claimed in the current weekly
//     window AND a drop is live to mint from. Shop shows the claim card;
//     the nav dot lights.
//   * "claimed"          — already claimed this window. Shop shows the
//     "Next free claim available in HH:MM:SS" countdown bar; dot dark.
//   * "nothing_to_claim" — no drop is live to mint from. Shop shows an
//     honest line, no countdown; dot dark.
//
// `next_reset_at` is the next Wednesday 11:00 Europe/Copenhagen (DST-aware),
// as an ISO instant — the countdown targets it.
//
// Cache key: ["free-claim"]. Invalidated by useClaimFreeDossier.onSuccess.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type FreeClaimState = "claimable" | "claimed" | "nothing_to_claim";

export interface FreeClaimData {
  state: FreeClaimState;
  next_reset_at: string;
  claimed_dossier_id: string | null;
  /** Set only on a fresh mint (POST) — which dossier was granted: the
   *  portfolio title of the randomly drawn drop + the minted edition.
   *  Null on GET + on idempotent re-claims. Optional so pre-existing
   *  test fixtures without the fields stay valid. */
  claimed_portfolio_title?: string | null;
  claimed_edition_number?: number | null;
}

export interface FreeClaimEnvelope {
  data: FreeClaimData;
}

export interface FreeClaimFetchError {
  code: string;
  message: string;
  status: number;
}

async function fetchFreeClaim(): Promise<FreeClaimEnvelope> {
  const response = await fetch("/api/v1/account/free-claim", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: FreeClaimFetchError = {
      code: "FREE_CLAIM_FETCH_FAILED",
      message: `Free claim status fetch failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as FreeClaimEnvelope;
}

export function useFreeClaim(options?: { enabled?: boolean }) {
  return useQuery<FreeClaimEnvelope, FreeClaimFetchError>({
    queryKey: ["free-claim"],
    queryFn: fetchFreeClaim,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
    // AppShell reads this SAME ["free-claim"] cache to light the Shop nav's
    // free-claim dot; it passes `enabled: signedIn` so the query never fires
    // for signed-out chrome. The /shop page leaves it enabled (default) —
    // one shared, 30s-stale cache entry serves both.
    enabled: options?.enabled ?? true,
  });
}
